import { ChangeDetectorRef, Component, ViewChild, effect, inject, input, signal, untracked } from '@angular/core';
import { CommonModule } from '@angular/common';
import { AbstractControl, FormBuilder, FormGroup, ReactiveFormsModule, ValidationErrors, ValidatorFn, Validators } from '@angular/forms';
import { FaDuotoneIconComponent } from '@fortawesome/angular-fontawesome';
import { BaseListPageClass } from '../../../../core/base-classes/base-list-page-class';
import { ListConstants } from '../../../../core/constants/lists.constants';
import { ChartOfAccountCategory, DataEntityTypes } from '../../../../core/enums/data.enums';
import { ChartOfAccountListSortOptions } from '../../../../core/enums/sort-options.enums';
import { ModalActions, ModalTypes } from '../../../../core/enums/base.enums';
import { ChartOfAccountsPermission } from '../../../../core/enums/permissions.enums';
import { ChartOfAccountModel, CreateChartOfAccountRequest, UpdateChartOfAccountRequest } from '../../../../core/models/data/chart-of-account.model';
import { ServiceResultModel } from '../../../../core/models/service/service-result.model';
import { ChartOfAccountService } from '../../../../core/services/chart-of-account/chart-of-account.service';
import { BootstrapService } from '../../../../core/services/bootstrap.service';
import { IfHasPermissionDirective } from '../../../../core/directives/if-has-permission/if-has-permission.directive';
import { InViewPortDirective } from '../../../../core/directives/in-view-port.directive';
import { ResponsiveTableDirective } from '../../../../core/directives/responsive-table/responsive-table.directive';
import { ResponsiveTableColumnDirective } from '../../../../core/directives/responsive-table/responsive-table-column.directive';
import { TooltipDirective } from '../../../../core/directives/tooltip.directive';
import { EscCloseDirective } from '../../../../core/directives/esc-close.directive';
import { FormatLabelPipe } from '../../../../core/pipes/format-label/format-label.pipe';
import { NoValuePipe } from '../../../../core/pipes/no-value/no-value.pipe';
import { ConfirmationModalComponent } from '../../../../parts/confirmation-modal/confirmation-modal.component';
import { InfoModalComponent } from '../../../../parts/info-modal/info-modal.component';

@Component({
	selector: 'ms-chart-of-account-category-section',
	standalone: true,
	imports: [
		CommonModule,
		ReactiveFormsModule,
		FaDuotoneIconComponent,
		IfHasPermissionDirective,
		InViewPortDirective,
		ResponsiveTableDirective,
		ResponsiveTableColumnDirective,
		TooltipDirective,
		EscCloseDirective,
		FormatLabelPipe,
		NoValuePipe,
		ConfirmationModalComponent,
		InfoModalComponent
	],
	templateUrl: './chart-of-account-category-section.component.html',
	styleUrl: './chart-of-account-category-section.component.scss'
})
export class ChartOfAccountCategorySectionComponent extends BaseListPageClass {

	//#region Dependencies

	private fb = inject(FormBuilder);
	private chartOfAccountService = inject(ChartOfAccountService);
	private bootstrapService = inject(BootstrapService);
	private changeDetector = inject(ChangeDetectorRef);

	public ChartOfAccountsPermission = ChartOfAccountsPermission;
	public DataEntityTypes = DataEntityTypes;

	//#endregion Dependencies

	//#region Inputs

	public category = input.required<ChartOfAccountCategory>();
	public organizationId = input<number | undefined>();
	public searchTerm = input<string>('');
	public isActive = input<boolean | undefined>(true);
	public sortOption = input<string>(ChartOfAccountListSortOptions.AccountNumberAsc);

	public range = () => ListConstants.CHART_OF_ACCOUNT_NUMBER_RANGES()[this.category()];

	// Mirrors the accent-per-category convention already used on the Trial Balance report
	// (AssetsCash gets its own accent, distinct from Assets, since they render as separate sections here).
	private static readonly CATEGORY_ACCENTS: Record<ChartOfAccountCategory, string> = {
		[ChartOfAccountCategory.Assets]: 'primary',
		[ChartOfAccountCategory.AssetsCash]: 'info',
		[ChartOfAccountCategory.Liabilities]: 'warning',
		[ChartOfAccountCategory.Equity]: 'secondary',
		[ChartOfAccountCategory.Revenue]: 'success',
		[ChartOfAccountCategory.Expenses]: 'danger'
	};

	public get accentColor(): string {
		return ChartOfAccountCategorySectionComponent.CATEGORY_ACCENTS[this.category()];
	}

	public get sectionClasses(): string {
		return `coa-category-section coa-accent-${this.accentColor}`;
	}

	//#endregion Inputs

	//#region State

	public accounts = signal<ChartOfAccountModel[]>([]);

	// Placeholder rows/cards shown while the initial load is in flight, so the section isn't
	// just a bare header the moment the page mounts.
	public readonly skeletonRows = [0, 1, 2];

	public addForm!: FormGroup;

	public editingId = signal<number | null>(null);
	public editForm!: FormGroup;

	public isSavingRow = signal<boolean>(false);
	public rowError = signal<string | undefined>(undefined);

	public isSubmittingStatusChange = false;
	private targetAccount?: ChartOfAccountModel;

	public get retireModalId(): string { return `coa-retire-modal-${this.category()}`; }
	public get infoModalId(): string { return `coa-info-modal-${this.category()}`; }
	public get addModalId(): string { return `coa-add-modal-${this.category()}`; }

	@ViewChild('retireConfirmationModalRef') retireConfirmationModal!: ConfirmationModalComponent;
	@ViewChild('infoModalRef') infoModalRef!: InfoModalComponent;

	//#endregion State

	//#region Lifecycle

	constructor() {
		super();

		// The add-account modal is always present in the DOM (Bootstrap just toggles its visibility),
		// so the form backing it must exist before the template first renders, not lazily on open.
		this.addForm = this.buildAddForm();

		// Reload from scratch whenever the shared filter bar (org, search, status, sort) changes.
		// Reads of other signals inside loadAccounts must not become dependencies of this effect.
		effect(() => {
			const organizationId = this.organizationId();
			this.searchTerm();
			this.isActive();
			this.sortOption();

			if (organizationId) {
				untracked(() => this.loadAccounts(false));
			} else {
				this.accounts.set([]);
			}
		});
	}

	//#endregion Lifecycle

	//#region Data Loading

	public async loadAccounts(append: boolean): Promise<void> {
		const organizationId = this.organizationId();
		if (!organizationId) return;

		this.isLoading = true;
		if (!append) { this.resetPagination(); }

		const result = await this.chartOfAccountService.searchChartOfAccounts(
			{
				organizationId,
				categories: [this.category()],
				isActive: this.isActive(),
				searchTerm: this.searchTerm() || undefined
			},
			this.sortOption(),
			this.skip(),
			this.take
		);

		this.totalCount = result.data?.total ?? 0;
		this.hasNext = result.data?.hasNext ?? false;

		if (result.success && result.data?.data) {
			this.accounts.set(append ? [...this.accounts(), ...result.data.data] : result.data.data);
		} else if (!append) {
			this.accounts.set([]);
		}

		this.isLoading = false;
		this.changeDetector.detectChanges();
	}

	public loadMoreInView(): void {
		if (!this.canLoadMore || !this.hasNext) return;

		this.canLoadMore = false;
		// Offset the next page from the rows actually loaded rather than skip + take, so a page that
		// failed to load can't push the offset past the list and strand the rest of the category.
		this.skip.set(this.accounts().length);
		this.loadAccounts(true);
		this.loadMoreDebounce();
	}

	//#endregion Data Loading

	//#region Add Account

	private buildAddForm(): FormGroup {
		return this.fb.group({
			accountNumber: [null, [Validators.required, this.rangeValidator()]],
			name: ['', [Validators.required, Validators.maxLength(255)]],
			description: ['', [Validators.maxLength(255)]]
		});
	}

	public openAddModal(): void {
		this.addForm = this.buildAddForm();
		this.rowError.set(undefined);
		this.bootstrapService.manageModal(`#${this.addModalId}`, ModalActions.Show);
	}

	public cancelAdd(): void {
		this.bootstrapService.manageModal(`#${this.addModalId}`, ModalActions.Hide);
		this.rowError.set(undefined);
	}

	public async saveAdd(): Promise<void> {
		if (this.addForm.invalid) {
			this.addForm.markAllAsTouched();
			this.changeDetector.detectChanges();
			return;
		}

		const organizationId = this.organizationId();
		if (!organizationId) return;

		this.isSavingRow.set(true);
		this.rowError.set(undefined);

		const value = this.addForm.getRawValue();
		const request: CreateChartOfAccountRequest = {
			organizationId,
			categoryId: this.category(),
			accountNumber: Number(value.accountNumber),
			name: value.name,
			description: value.description || undefined
		};

		const result = await this.chartOfAccountService.createChartOfAccount(request);

		if (result.success) {
			this.bootstrapService.manageModal(`#${this.addModalId}`, ModalActions.Hide);
			await this.loadAccounts(false);
		} else {
			this.rowError.set(this.resolveErrorMessage(result));
		}

		this.isSavingRow.set(false);
		this.changeDetector.detectChanges();
	}

	//#endregion Add Account

	//#region Edit Account

	public startEdit(account: ChartOfAccountModel): void {
		this.editingId.set(account.chartOfAccountId);
		this.rowError.set(undefined);
		this.editForm = this.fb.group({
			accountNumber: [account.accountNumber, [Validators.required, this.rangeValidator()]],
			name: [account.name, [Validators.required, Validators.maxLength(255)]],
			description: [account.description ?? '', [Validators.maxLength(255)]]
		});
	}

	public cancelEdit(): void {
		this.editingId.set(null);
		this.rowError.set(undefined);
	}

	public async saveEdit(account: ChartOfAccountModel): Promise<void> {
		if (this.editForm.invalid) {
			this.editForm.markAllAsTouched();
			this.changeDetector.detectChanges();
			return;
		}

		this.isSavingRow.set(true);
		this.rowError.set(undefined);

		const value = this.editForm.getRawValue();
		const request: UpdateChartOfAccountRequest = {
			accountNumber: Number(value.accountNumber),
			name: value.name,
			description: value.description || undefined
		};

		const result = await this.chartOfAccountService.updateChartOfAccount(account.chartOfAccountId, request);

		if (result.success) {
			this.editingId.set(null);
			await this.loadAccounts(false);
		} else {
			this.rowError.set(this.resolveErrorMessage(result));
		}

		this.isSavingRow.set(false);
		this.changeDetector.detectChanges();
	}

	//#endregion Edit Account

	//#region Retire / Reactivate

	public confirmStatusChange(account: ChartOfAccountModel): void {
		this.targetAccount = account;
		const isRetiring = account.isActive;

		this.retireConfirmationModal.modalType.set(ModalTypes.Question);
		this.retireConfirmationModal.title.set(isRetiring ? 'Retire Account' : 'Reactivate Account');
		this.retireConfirmationModal.message.set(
			isRetiring
				? `Retire "${account.accountNumber} - ${account.name}"? It will no longer be selectable in pickers, but historical activity stays visible in reports and account history.`
				: `Reactivate "${account.accountNumber} - ${account.name}"? It will become selectable in pickers again.`
		);
		this.retireConfirmationModal.confirmButtonText.set(isRetiring ? 'Retire' : 'Reactivate');
		this.retireConfirmationModal.cancelButtonText.set('Cancel');
		this.retireConfirmationModal.enableNotes.set(true);
		this.retireConfirmationModal.isNoteRequired.set(false);

		this.bootstrapService.manageModal(`#${this.retireModalId}`, ModalActions.Show);
	}

	public async performStatusChange(note?: string): Promise<void> {
		if (!this.targetAccount) return;

		const account = this.targetAccount;
		const activate = !account.isActive;

		this.isSubmittingStatusChange = true;
		const result = await this.chartOfAccountService.updateChartOfAccountStatus(account.chartOfAccountId, activate, note);
		this.isSubmittingStatusChange = false;

		this.bootstrapService.manageModal(`#${this.retireModalId}`, ModalActions.Hide);

		if (result.success) {
			await this.loadAccounts(false);
		} else {
			this.infoModalRef.modalType.set(ModalTypes.Failure);
			this.infoModalRef.message.set(result.message || "We were unable to update this account's status.");
			this.bootstrapService.manageModal(`#${this.infoModalId}`, ModalActions.Show);
		}

		this.targetAccount = undefined;
	}

	public cancelStatusChange(): void {
		this.bootstrapService.manageModal(`#${this.retireModalId}`, ModalActions.Hide);
		this.targetAccount = undefined;
	}

	//#endregion Retire / Reactivate

	//#region Field Validation Messages

	public getAccountNumberError(form: FormGroup): string | undefined {
		const control = form.get('accountNumber');
		if (!control || !control.invalid || !control.touched) return undefined;
		if (control.errors?.['required']) return 'Account number is required.';
		if (control.errors?.['outOfRange']) {
			const range = control.errors['outOfRange'];
			return `Must be between ${range.min} and ${range.max} for this category.`;
		}
		return 'Enter a valid account number.';
	}

	public getNameError(form: FormGroup): string | undefined {
		const control = form.get('name');
		if (!control || !control.invalid || !control.touched) return undefined;
		if (control.errors?.['required']) return 'Name is required.';
		if (control.errors?.['maxlength']) return 'Name is too long.';
		return undefined;
	}

	public getDescriptionError(form: FormGroup): string | undefined {
		const control = form.get('description');
		if (!control || !control.invalid || !control.touched) return undefined;
		if (control.errors?.['maxlength']) return 'Description is too long.';
		return undefined;
	}

	//#endregion Field Validation Messages

	//#region Private Helpers

	private rangeValidator(): ValidatorFn {
		return (control: AbstractControl): ValidationErrors | null => {
			if (control.value === null || control.value === undefined || control.value === '') return null;
			const value = Number(control.value);
			if (Number.isNaN(value)) return null;
			const range = this.range();
			return (value < range.min || value > range.max) ? { outOfRange: range } : null;
		};
	}

	private resolveErrorMessage(result: ServiceResultModel<ChartOfAccountModel>): string {
		return result.message || 'We were unable to save this account.';
	}

	//#endregion Private Helpers
}
